import React, { useState } from 'react';
import { Alert, Button, Group, Select, Text } from '@mantine/core';
import { IconAlertTriangle, IconArrowBackUp, IconCategory, IconInfoCircle, IconRefresh, IconUsers, IconUserQuestion } from '@tabler/icons-react';
import { ImportDraft, ImportRecipe } from '@/services/import-draft/import-draft-model';
import {
  categoryMoves, columnLabel, contactNameMapped, hasTargetField, mapContactName, problemsWithCode, suggestedContactColumn, typeRestored,
} from '@/services/import-draft/import-draft-helpers';

const SAVED_DEFAULT = 'The columns match a mapping saved from an earlier import.';

export function SavedRecipeNotice({ draft, onRedetect, busy }: { draft: ImportDraft; onRedetect?: () => void; busy?: boolean }) {
  const reason = draft.ModeReason && /saved/i.test(draft.ModeReason) ? draft.ModeReason : SAVED_DEFAULT;
  return (
    <Alert color="blue" icon={<IconRefresh size={16} />} title="Saved mapping reused" data-testid="saved-recipe-notice">
      <Text size="sm">{reason}{draft.SourceLabel ? ` (${draft.SourceLabel})` : ''}</Text>
      <Text size="sm" c="dimmed">If this file is laid out differently from the last one, match the columns again from scratch.</Text>
      {onRedetect && (
        <Button mt="xs" size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={onRedetect} loading={busy} data-testid="redetect">
          Re-detect
        </Button>
      )}
    </Alert>
  );
}

export function MixedContactsNotice({ draft }: { draft: ImportDraft }) {
  const problems = problemsWithCode(draft.Problems, 'MixedContacts');
  if (!problems.length) return null;
  return (
    <Alert color="yellow" icon={<IconUsers size={16} />} title="This file may include suppliers or other contacts that are not customers" data-testid="mixed-contacts-notice">
      {problems.map((p, i) => <Text size="sm" key={i}>{p.Message}</Text>)}
      <Text size="sm" mt={4}>Every row is imported as a customer. Remove the rows that are not customers from the file, or add an instruction such as "Skip rows where Type is Supplier" and remap.</Text>
    </Alert>
  );
}

interface ContactNameNoticeProps {
  draft: ImportDraft;
  recipe: ImportRecipe;
  onRecipeChange: (recipe: ImportRecipe) => void;
  busy?: boolean;
}

export function ContactNameNotice({ draft, recipe, onRecipeChange, busy }: ContactNameNoticeProps) {
  const [chosen, setChosen] = useState<string | null>(null);
  const problem = problemsWithCode(draft.Problems, 'ContactNameUnmapped')[0];
  if (!problem || contactNameMapped(recipe)) return null;

  const profiles = draft.Profiles || [];
  const suggested = suggestedContactColumn(problem, profiles);
  const targetDescription = hasTargetField(draft.TargetFields, 'FullName') ? 'the contact person' : 'the contact first and last name';

  function apply(columnIndex: number) {
    onRecipeChange(mapContactName(recipe, draft.TargetFields, columnIndex));
  }

  return (
    <Alert color="orange" icon={<IconUserQuestion size={16} />} title="No contact person column is mapped" data-testid="contact-name-notice">
      <Text size="sm">No contact person column is mapped. ServCraft will use the customer name as the contact person.</Text>
      {suggested !== null ? (
        <>
          <Text size="sm" mt={4}>The column <b>{columnLabel(profiles, suggested)}</b> looks like it holds the contact person.</Text>
          <Button mt="xs" size="xs" color="orange" onClick={() => apply(suggested)} disabled={busy} data-testid="map-contact-name">
            Use {columnLabel(profiles, suggested)} as {targetDescription}
          </Button>
        </>
      ) : (
        <Group mt="xs" gap="xs" align="flex-end">
          <Select
            size="xs"
            placeholder="Choose the column with the contact person"
            data={profiles.filter(p => p.Header || p.NonEmptyCount > 0).map(p => ({ value: String(p.Index), label: columnLabel(profiles, p.Index) }))}
            value={chosen}
            onChange={setChosen}
            searchable
            disabled={busy}
            aria-label="Contact person column"
            w={280}
          />
          <Button size="xs" color="orange" disabled={busy || chosen === null} onClick={() => chosen !== null && apply(Number(chosen))} data-testid="map-contact-name">
            Use this column
          </Button>
        </Group>
      )}
    </Alert>
  );
}

export function RetiredTargetNotice({ draft }: { draft: ImportDraft }) {
  const problems = problemsWithCode(draft.Problems, 'RetiredTarget');
  if (!problems.length) return null;
  return (
    <Alert color="blue" icon={<IconInfoCircle size={16} />} title="Some columns cannot be stored by ServCraft" data-testid="retired-target-notice">
      {problems.map((p, i) => <Text size="sm" key={i}>{p.Message}</Text>)}
      <Text size="sm" c="dimmed" mt={4}>These columns have been left out of the import. Nothing else needs to change.</Text>
    </Alert>
  );
}

interface CategoryMoveNoticeProps {
  draft: ImportDraft;
  recipe: ImportRecipe | null;
  onUndo?: (columnIndex: number) => void;
  busy?: boolean;
}

export function CategoryMoveNotice({ draft, recipe, onUndo, busy }: CategoryMoveNoticeProps) {
  const moves = categoryMoves(draft.Problems, recipe).filter(m => !typeRestored(recipe, m.columnIndex));
  if (!moves.length) return null;
  const profiles = draft.Profiles || [];
  return (
    <Alert color="orange" icon={<IconCategory size={16} />} title="A column was used as the category instead of the type" data-testid="type-category-notice">
      {moves.map((m, i) => (
        <Group key={i} justify="space-between" align="flex-start" wrap="nowrap" mt={i ? 'xs' : 0}>
          <Text size="sm" style={{ flex: 1 }}>{m.problem.Message}</Text>
          {onUndo && m.columnIndex !== null && (
            <Button
              size="xs"
              variant="light"
              color="orange"
              leftSection={<IconArrowBackUp size={14} />}
              onClick={() => onUndo(m.columnIndex as number)}
              disabled={busy}
              data-testid="undo-category-move"
            >
              Undo, use {columnLabel(profiles, m.columnIndex)} as Type
            </Button>
          )}
        </Group>
      ))}
    </Alert>
  );
}

export function ProblemInline({ message, onIgnore, busy }: { message: string; onIgnore?: () => void; busy?: boolean }) {
  return (
    <Group gap={6} mt={4} wrap="nowrap" align="flex-start" data-testid="problem-inline">
      <IconAlertTriangle size={14} color="var(--mantine-color-orange-7)" style={{ flexShrink: 0, marginTop: 2 }} />
      <Text size="xs" c="orange.8" style={{ flex: 1 }}>{message}</Text>
      {onIgnore && <Button size="compact-xs" variant="light" color="orange" onClick={onIgnore} disabled={busy}>Ignore</Button>}
    </Group>
  );
}
